/**
 * The gap object as written to the workflow-run collection — the raw output of
 * one gap-analysis run, before any refinement.
 *
 * This is NOT the final `Gap` (see `gap.domain.ts`). The distinction matters:
 * this shape carries what the FINDER and VALIDATOR establish — whether a gap
 * exists, and its first-pass scores (confidence, impact, urgency, missability,
 * customer friction). The finder scores each gap; the validator returns its own
 * revised scores alongside the verdict. A later refinement phase re-derives the
 * final presentation from these.
 *
 * Delivery and sales run parallel pipelines but share one scoring vocabulary:
 * every scale below applies identically to both. What remains
 * variant-specific is the detector and claim-type vocabularies, and one
 * sales-only control flag.
 */
// ---------------------------------------------------------------------------
// Shared scales
// ---------------------------------------------------------------------------
/**
 * How confident the finder is that the gap is real. Unified across both
 * variants on the delivery scale — sales previously used a 3-step scale.
 */
export var GapRunConfidence;
(function (GapRunConfidence) {
    GapRunConfidence["LOW"] = "LOW";
    GapRunConfidence["LOW_MEDIUM"] = "LOW_MEDIUM";
    GapRunConfidence["MEDIUM"] = "MEDIUM";
    GapRunConfidence["MEDIUM_HIGH"] = "MEDIUM_HIGH";
    GapRunConfidence["HIGH"] = "HIGH";
})(GapRunConfidence || (GapRunConfidence = {}));
/**
 * The three-step scale shared by `impact`, `urgency` and `customerFriction`.
 * Unified across both variants: delivery previously scored impact on the
 * five-step confidence scale, sales on this one.
 */
export var GapRunLevel;
(function (GapRunLevel) {
    GapRunLevel["LOW"] = "LOW";
    GapRunLevel["MEDIUM"] = "MEDIUM";
    GapRunLevel["HIGH"] = "HIGH";
})(GapRunLevel || (GapRunLevel = {}));
/**
 * How likely the gap is to be missed without this analysis. `EXTREME` sits
 * above `HIGH`: certainly written nowhere, and missed unless surfaced.
 */
export var GapRunMissability;
(function (GapRunMissability) {
    GapRunMissability["LOW"] = "LOW";
    GapRunMissability["MEDIUM"] = "MEDIUM";
    GapRunMissability["HIGH"] = "HIGH";
    GapRunMissability["EXTREME"] = "EXTREME";
})(GapRunMissability || (GapRunMissability = {}));
/**
 * {@link GapRunLevel} plus `NONE`, which the validator uses only on a
 * CONTRADICTED gap — a killed gap has no residual friction to weigh.
 */
export var GapRunRevisedLevel;
(function (GapRunRevisedLevel) {
    GapRunRevisedLevel["NONE"] = "NONE";
    GapRunRevisedLevel["LOW"] = "LOW";
    GapRunRevisedLevel["MEDIUM"] = "MEDIUM";
    GapRunRevisedLevel["HIGH"] = "HIGH";
})(GapRunRevisedLevel || (GapRunRevisedLevel = {}));
/** {@link GapRunMissability} plus `NONE`, on the same CONTRADICTED-only rule. */
export var GapRunRevisedMissability;
(function (GapRunRevisedMissability) {
    GapRunRevisedMissability["NONE"] = "NONE";
    GapRunRevisedMissability["LOW"] = "LOW";
    GapRunRevisedMissability["MEDIUM"] = "MEDIUM";
    GapRunRevisedMissability["HIGH"] = "HIGH";
    GapRunRevisedMissability["EXTREME"] = "EXTREME";
})(GapRunRevisedMissability || (GapRunRevisedMissability = {}));
/** The validator's ruling on a candidate gap. Identical in both pipelines. */
export var GapRunVerdict;
(function (GapRunVerdict) {
    GapRunVerdict["CONFIRMED"] = "CONFIRMED";
    GapRunVerdict["WEAKENED"] = "WEAKENED";
    GapRunVerdict["CONTRADICTED"] = "CONTRADICTED";
    GapRunVerdict["UNVERIFIABLE"] = "UNVERIFIABLE";
})(GapRunVerdict || (GapRunVerdict = {}));
/**
 * Which finder lens surfaced the gap. Unified on the sales vocabulary; delivery
 * previously used a free string written by the merge agent.
 */
export var GapRunFoundBy;
(function (GapRunFoundBy) {
    GapRunFoundBy["COMMON_SENSE"] = "COMMON_SENSE";
    GapRunFoundBy["ANALOG"] = "ANALOG";
    GapRunFoundBy["COMMON_SENSE_AND_ANALOG"] = "COMMON_SENSE_AND_ANALOG";
})(GapRunFoundBy || (GapRunFoundBy = {}));
/**
 * Whether the source fact behind the gap is still current. A gap resting on a
 * superseded draft is not a gap — this is validity, not prioritisation, so it
 * stays in this phase.
 */
export var GapRunChronologyVerdict;
(function (GapRunChronologyVerdict) {
    GapRunChronologyVerdict["CURRENT_FACT"] = "CURRENT_FACT";
    GapRunChronologyVerdict["SUPERSEDED_DRAFT"] = "SUPERSEDED_DRAFT";
    GapRunChronologyVerdict["ACCEPTED_REVISION"] = "ACCEPTED_REVISION";
    GapRunChronologyVerdict["UNRESOLVED_CONFLICT"] = "UNRESOLVED_CONFLICT";
    GapRunChronologyVerdict["CHRONOLOGY_UNKNOWN"] = "CHRONOLOGY_UNKNOWN";
})(GapRunChronologyVerdict || (GapRunChronologyVerdict = {}));
// ---------------------------------------------------------------------------
// Variant vocabularies
// ---------------------------------------------------------------------------
/** Delivery detectors. Previously a free string described only as "D1–D7". */
export var DeliveryGapDetector;
(function (DeliveryGapDetector) {
    DeliveryGapDetector["D1"] = "D1";
    DeliveryGapDetector["D2"] = "D2";
    DeliveryGapDetector["D3"] = "D3";
    DeliveryGapDetector["D4"] = "D4";
    DeliveryGapDetector["D5"] = "D5";
    DeliveryGapDetector["D6"] = "D6";
    DeliveryGapDetector["D7"] = "D7";
    DeliveryGapDetector["D8"] = "D8";
})(DeliveryGapDetector || (DeliveryGapDetector = {}));
/** Sales detectors: C1–C8 common-sense, A1–A4 analog, H hypothesis. */
export var SalesGapDetector;
(function (SalesGapDetector) {
    SalesGapDetector["C1"] = "C1";
    SalesGapDetector["C2"] = "C2";
    SalesGapDetector["C3"] = "C3";
    SalesGapDetector["C4"] = "C4";
    SalesGapDetector["C5"] = "C5";
    SalesGapDetector["C6"] = "C6";
    SalesGapDetector["C7"] = "C7";
    SalesGapDetector["C8"] = "C8";
    SalesGapDetector["A1"] = "A1";
    SalesGapDetector["A2"] = "A2";
    SalesGapDetector["A3"] = "A3";
    SalesGapDetector["A4"] = "A4";
    SalesGapDetector["H"] = "H";
})(SalesGapDetector || (SalesGapDetector = {}));
export var DeliveryGapClaimType;
(function (DeliveryGapClaimType) {
    DeliveryGapClaimType["MISSING_TASK"] = "MISSING_TASK";
    DeliveryGapClaimType["WRONG_ESTIMATE"] = "WRONG_ESTIMATE";
    DeliveryGapClaimType["COMMITMENT_WITHOUT_PLAN_ITEM"] = "COMMITMENT_WITHOUT_PLAN_ITEM";
    DeliveryGapClaimType["RESOURCING"] = "RESOURCING";
    DeliveryGapClaimType["REPEATED_FAILURE_PATTERN"] = "REPEATED_FAILURE_PATTERN";
    DeliveryGapClaimType["OTHER"] = "OTHER";
})(DeliveryGapClaimType || (DeliveryGapClaimType = {}));
export var SalesGapClaimType;
(function (SalesGapClaimType) {
    SalesGapClaimType["REQUIREMENT_COVERAGE"] = "REQUIREMENT_COVERAGE";
    SalesGapClaimType["SCOPE_OR_COMMITMENT_DRIFT"] = "SCOPE_OR_COMMITMENT_DRIFT";
    SalesGapClaimType["COMMERCIAL_OR_PRICING"] = "COMMERCIAL_OR_PRICING";
    SalesGapClaimType["FEASIBILITY_OR_EXTERNAL_FACT"] = "FEASIBILITY_OR_EXTERNAL_FACT";
    SalesGapClaimType["TIMELINE_OR_VALIDITY"] = "TIMELINE_OR_VALIDITY";
    SalesGapClaimType["QUALIFICATION_OR_BID_DECISION"] = "QUALIFICATION_OR_BID_DECISION";
    SalesGapClaimType["BID_TEAM_OR_DEPENDENCY_READINESS"] = "BID_TEAM_OR_DEPENDENCY_READINESS";
    SalesGapClaimType["SALES_TO_DELIVERY_HANDOFF"] = "SALES_TO_DELIVERY_HANDOFF";
    SalesGapClaimType["RECURRING_FAILURE_MECHANISM"] = "RECURRING_FAILURE_MECHANISM";
    SalesGapClaimType["ASSUMPTION_VS_ANALOG_OUTCOME"] = "ASSUMPTION_VS_ANALOG_OUTCOME";
    SalesGapClaimType["BUYER_OR_COMPETITOR_PATTERN"] = "BUYER_OR_COMPETITOR_PATTERN";
    SalesGapClaimType["MISSING_STEP_VS_ANALOG_PLAYBOOK"] = "MISSING_STEP_VS_ANALOG_PLAYBOOK";
    SalesGapClaimType["OTHER"] = "OTHER";
})(SalesGapClaimType || (SalesGapClaimType = {}));
